import { useMemo } from "react";
import { useForm } from "react-hook-form";
import useEvent from "react-use-event-hook";

import {
  usePassengersBulkActions,
  usePassengersMapBulkActions,
  useSelectedIds,
  useYupValidationResolver,
} from "@ryde/redux";
import {
  AddBulkStationFormFieldsEnum,
  AddBulkStationMethodEnum,
  RydeProcessTitle,
  useOnProcessEnd,
} from "@ryde/shared";

import { yupAddBulkStationsValidationSchema } from "../validations";

export const usePassengerMapAddBulkStationsPanel = () => {
  const { selectedRowsIds } = useSelectedIds("passenger");
  const { bulkRefetchPassengersStationMapData } = usePassengersMapBulkActions();
  const { handleBulkFindNearbyStation } = usePassengersBulkActions();

  const { startListening } = useOnProcessEnd({
    entity: "passenger",
    processTitle: RydeProcessTitle.BulkSetPassengersNearBy,
    onProcessEnd: () => {
      bulkRefetchPassengersStationMapData();
    },
  });

  const resolver = useYupValidationResolver(
    yupAddBulkStationsValidationSchema,
    "passenger",
  );

  const methods = useForm<{
    method: AddBulkStationMethodEnum;
    value: number | null;
    isSetOnlyForMissingPickup: boolean;
  }>({
    mode: "all",
    reValidateMode: "onChange",
    criteriaMode: "firstError",
    defaultValues: {
      method: AddBulkStationMethodEnum.distanceInMeters,
      value: null,
      isSetOnlyForMissingPickup: true,
    },
    resolver,
  });

  const {
    getValues,
    trigger,
    watch,
    formState: { isValid },
  } = methods;

  const methodWatchedValue = watch(AddBulkStationFormFieldsEnum.method);
  const valueWatched = watch(AddBulkStationFormFieldsEnum.value);

  const isDisabled = useMemo(() => {
    const value = Number(valueWatched);
    const isValueValid = Number.isFinite(value) && value > 0;

    return !(
      isValid &&
      methodWatchedValue &&
      isValueValid &&
      selectedRowsIds?.length
    );
  }, [isValid, methodWatchedValue, selectedRowsIds.length, valueWatched]);

  const handleClick = useEvent(async (): Promise<void> => {
    const isValidAfterTrigger = await trigger();

    if (!isValidAfterTrigger) {
      return;
    }

    const { method, value, isSetOnlyForMissingPickup } = getValues();

    const calcMethod =
      method === AddBulkStationMethodEnum.distanceInMeters
        ? "distanceInMeters"
        : "walkTimeMinute";

    const updateResult = await handleBulkFindNearbyStation(
      {
        ids: selectedRowsIds,
        isSetOnlyForMissingPickup,
        isAllowBusStationSearch: true,
        isOnlyActiveStation: true,
        [calcMethod]: value,
      },
      true,
    );

    if (!updateResult.success) {
      return;
    }

    startListening();
  });

  return {
    methods,
    handleClick,
    isDisabled,
  };
};
